// src/components/AiUsageCard/index.js — cartão "Uso de IA" da empresa (Configurações, só admin)
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import LinearProgress from "@material-ui/core/LinearProgress";
import Grid from "@material-ui/core/Grid";
import Table from "@material-ui/core/Table";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableCell from "@material-ui/core/TableCell";
import TableBody from "@material-ui/core/TableBody";
import { makeStyles } from "@material-ui/core/styles";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getTokens } from "../../styles/tokens";
import { StatusBadge } from "../ui";

const useStyles = makeStyles((theme) => {
  const t = getTokens(theme.palette.type, theme.palette.primary.main);
  return {
    root: {
      padding: 20,
      marginTop: theme.spacing(2),
      backgroundColor: t.paper,
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.md,
      boxShadow: t.shadow.card,
    },
    title: { fontSize: 15, fontWeight: 600, lineHeight: 1.4, color: t.text },
    hint: { fontSize: 13, lineHeight: 1.5, color: t.textSecondary, marginTop: 2 },
    bar: {
      height: 8,
      borderRadius: 4,
      margin: theme.spacing(2, 0),
      backgroundColor: t.tint.soft,
      "& .MuiLinearProgress-bar": { backgroundColor: t.brand, borderRadius: 4 },
    },
    barOver: {
      "& .MuiLinearProgress-bar": { backgroundColor: t.semantic.warning },
    },
    label: {
      display: "block",
      fontSize: 12.5,
      fontWeight: 500,
      color: t.textSecondary,
      marginBottom: 2,
    },
    num: { fontSize: 20, fontWeight: 600, color: t.text, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.01em" },
    sub: { fontSize: 13, fontWeight: 600, color: t.text, marginTop: theme.spacing(2.5) },
    tableBox: {
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      marginTop: theme.spacing(2),
      overflow: "hidden",
      "& tbody tr:last-child td": { borderBottom: 0 },
    },
    dayBox: {
      maxHeight: 220,
      overflowY: "auto",
      border: `1px solid ${t.line}`,
      borderRadius: t.radius.sm,
      marginTop: 8,
      "& tbody tr:last-child td": { borderBottom: 0 },
    },
    empty: { fontSize: 13, color: t.textMuted, padding: "8px 0" },
    over: { color: t.semantic.warning },
    keySection: {
      marginTop: theme.spacing(2.5),
      paddingTop: theme.spacing(2.5),
      borderTop: `1px solid ${t.line}`,
    },
    keyHeader: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    keyTitle: { fontSize: 13, fontWeight: 600, color: t.text },
    keyLabel: { display: "block", fontSize: 12.5, fontWeight: 600, color: t.textSecondary, margin: "12px 0 6px" },
    keyRow: { display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" },
    keyField: { flex: "1 1 260px" },
    keyIcon: { fontSize: 18, marginRight: 8, color: t.textMuted },
  };
});

export const brl = (v) =>
  `R$ ${Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const dayLabel = (day) => (typeof day === "string" ? day.split("-").reverse().join("/") : "");

// Chave OpenAI da própria empresa: com ela o consumo não é medido (a empresa paga a OpenAI direto).
// O backend nunca devolve a chave — só `hasKey` e os últimos 4 caracteres.
const CompanyKeySection = ({ classes }) => {
  const [info, setInfo] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/ai-usage/company-key");
        if (active) setInfo(data);
      } catch (err) {
        // 403/404 = não é admin ou backend antigo → seção fica escondida
        const st = err?.response?.status;
        if (st !== 403 && st !== 404) toastError(err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!info) return null;

  const save = async () => {
    const value = apiKey.trim();
    if (!value) return;
    setBusy(true);
    try {
      const { data } = await api.put("/ai-usage/company-key", { apiKey: value });
      setInfo(data);
      setApiKey("");
      toast.success(i18n.t("aiUsage.companyKey.saved"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/ai-usage/company-key", { apiKey: null });
      setInfo(data);
      setApiKey("");
      toast.success(i18n.t("aiUsage.companyKey.removed"));
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={classes.keySection}>
      <div className={classes.keyHeader}>
        <Typography className={classes.keyTitle}>{i18n.t("aiUsage.companyKey.title")}</Typography>
        {info.hasKey ? (
          <StatusBadge
            color="success"
            size="sm"
            icon={<LockOutlinedIcon style={{ fontSize: 13 }} />}
            label={i18n.t("aiUsage.companyKey.set", { last4: info.last4 || "" })}
          />
        ) : (
          <StatusBadge color="neutral" size="sm" dot={false} label={i18n.t("aiUsage.companyKey.usingPlatform")} />
        )}
      </div>
      <Typography className={classes.hint}>{i18n.t("aiUsage.companyKey.help")}</Typography>
      <label className={classes.keyLabel} htmlFor="ai-usage-company-key">
        {i18n.t("aiUsage.companyKey.field")}
      </label>
      <div className={classes.keyRow}>
        <TextField
          id="ai-usage-company-key"
          className={classes.keyField}
          size="small"
          variant="outlined"
          type="password"
          autoComplete="new-password"
          placeholder={i18n.t("aiUsage.companyKey.placeholder")}
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          InputProps={{
            startAdornment: <VpnKeyOutlinedIcon className={classes.keyIcon} />,
          }}
        />
        <Button variant="contained" color="primary" disabled={busy || !apiKey.trim()} onClick={save}>
          {i18n.t("aiUsage.companyKey.save")}
        </Button>
        {info.hasKey && (
          <Button variant="outlined" disabled={busy} onClick={remove}>
            {i18n.t("aiUsage.companyKey.remove")}
          </Button>
        )}
      </div>
    </div>
  );
};

const AiUsageCard = () => {
  const classes = useStyles();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get("/ai-usage/summary");
        if (active) setData(data);
      } catch (err) {
        // 404 = backend sem o módulo de medição ainda → cartão fica escondido
        if (err?.response?.status !== 404) toastError(err);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!data) return null;

  const used = Number(data.usedBrl) || 0;
  const credit = Number(data.creditBrl) || 0;
  const cap = Number(data.capBrl) || 0;
  const pct = cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const overCredit = used > credit;
  const byKind = data.byKind || {};
  const byDay = Array.isArray(data.byDay) ? data.byDay : [];

  return (
    <Paper className={classes.root} elevation={0}>
      <Typography className={classes.title}>
        {i18n.t("aiUsage.title")} — {data.period}
      </Typography>
      {credit <= 0 && <Typography className={classes.hint}>{i18n.t("aiUsage.noCredit")}</Typography>}
      {!data.enforce && <Typography className={classes.hint}>{i18n.t("aiUsage.onlyMeasuring")}</Typography>}

      <LinearProgress
        className={`${classes.bar} ${overCredit ? classes.barOver : ""}`}
        variant="determinate"
        value={pct}
      />

      <Grid container spacing={2}>
        <Grid item xs={6} sm={3}>
          <span className={classes.label}>{i18n.t("aiUsage.used")}</span>
          <span className={`${classes.num} ${overCredit ? classes.over : ""}`}>{brl(used)}</span>
        </Grid>
        <Grid item xs={6} sm={3}>
          <span className={classes.label}>{i18n.t("aiUsage.credit")}</span>
          <span className={classes.num}>{brl(credit)}</span>
        </Grid>
        <Grid item xs={6} sm={3}>
          <span className={classes.label}>{i18n.t("aiUsage.extra")}</span>
          <span className={classes.num}>{brl(data.extraBrl)}</span>
        </Grid>
        <Grid item xs={6} sm={3}>
          <span className={classes.label}>{i18n.t("aiUsage.cap")}</span>
          <span className={classes.num}>{brl(cap)}</span>
        </Grid>
      </Grid>

      <div className={classes.tableBox}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{i18n.t("aiUsage.kinds.chat")}</TableCell>
              <TableCell>{i18n.t("aiUsage.kinds.transcription")}</TableCell>
              <TableCell>{i18n.t("aiUsage.kinds.tts")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>{brl(byKind.chat)}</TableCell>
              <TableCell>{brl(byKind.transcription)}</TableCell>
              <TableCell>{brl(byKind.tts)}</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>

      <Typography className={classes.sub}>{i18n.t("aiUsage.byDay")}</Typography>
      {byDay.length === 0 ? (
        <div className={classes.empty}>{i18n.t("aiUsage.noUsage")}</div>
      ) : (
        <div className={classes.dayBox}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>{i18n.t("aiUsage.date")}</TableCell>
                <TableCell align="right">{i18n.t("aiUsage.value")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {byDay.map((d) => (
                <TableRow key={d.day}>
                  <TableCell>{dayLabel(d.day)}</TableCell>
                  <TableCell align="right">{brl(d.brl)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CompanyKeySection classes={classes} />
    </Paper>
  );
};

export default AiUsageCard;
